/*
 * Copyright (c) 2026. World Wide Generation Ltd
 */

import { Fragment, useMemo, useState } from 'react';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { DataTransferMappingStatus, IntegrationValueSource, SurveyDataStatus, ValidationNoticeType, type DataTransferConnectionInfo, type DataTransferRow, type ToleranceResult, type ValidationNotice } from '@g17eco/types/data-transfer';
import { getRowKey, getStatusBadgeClass, getStatusLabel, getSurveyDataStatusLabel, getSurveyDataStatusClass, getSurveyUrl, getSourceInfo, renderUnitInfo } from './data-transfer-utils';

interface UtrGroup {
  utrCode: string;
  utrLabel: string;
  valueType: string;
  unit?: string;
  numberScale?: string;
  connectionInfo?: DataTransferConnectionInfo;
  rows: DataTransferRow[];
}

const groupRowsByUtr = (rows: DataTransferRow[]): UtrGroup[] => {
  const map = new Map<string, DataTransferRow[]>();
  for (const row of rows) {
    const group = map.get(row.utrCode);
    if (group) {
      group.push(row);
    } else {
      map.set(row.utrCode, [row]);
    }
  }
  return Array.from(map.entries()).map(([utrCode, groupRows]) => ({
    utrCode,
    utrLabel: groupRows[0].utrLabel,
    valueType: groupRows[0].valueType,
    unit: groupRows[0].survey.unit,
    numberScale: groupRows[0].survey.numberScale,
    connectionInfo: groupRows[0].connectionInfo,
    rows: groupRows,
  }));
};

const getRowClassName = (row: DataTransferRow): string => {
  if (row.notices.some((n) => n.type === ValidationNoticeType.Error)) {
    return 'table-danger';
  }
  if (row.notices.some((n) => n.type === ValidationNoticeType.Warning) || row.hasDiscrepancy) {
    return 'table-warning';
  }
  return '';
};

const getGroupClassName = (rows: DataTransferRow[]): string => {
  if (rows.some((r) => r.notices.some((n) => n.type === ValidationNoticeType.Error))) {
    return 'table-danger';
  }
  if (rows.some((r) => r.notices.some((n) => n.type === ValidationNoticeType.Warning) || r.hasDiscrepancy)) {
    return 'table-warning';
  }
  return '';
};


const renderStatusCell = (row: { status: DataTransferMappingStatus; verifiedAt?: string }) => (
  <div className='d-flex align-items-center gap-1'>
    <span className={`badge ${getStatusBadgeClass(row.status)}`}>{getStatusLabel(row.status)}</span>
    {row.verifiedAt && (
      <SimpleTooltip text={`Verified ${row.verifiedAt}`}>
        <i className='fa fa-check-circle text-ThemeSuccessMedium' />
      </SimpleTooltip>
    )}
  </div>
);


const getNoticeIcon = (type: ValidationNoticeType): string => {
  switch (type) {
    case ValidationNoticeType.Error:
      return 'fa-times-circle text-ThemeDangerMedium';
    case ValidationNoticeType.Warning:
      return 'fa-exclamation-triangle text-ThemeWarningMedium';
    case ValidationNoticeType.Info:
      return 'fa-info-circle text-ThemeInfoMedium';
  }
};


const renderValueUnit = (unit?: string) => {
  if (!unit) {
    return null;
  }
  return <small className='text-ThemeTextLight ms-1'>{unit}</small>;
};

const formatDisplayValue = (value: string | number): string => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
  const num = Number(value);
  if (!isNaN(num) && value.length > 8) {
    return num.toFixed(2);
  }
  return value;
};

const renderIntegrationValue = (integration: DataTransferRow['integration']) => {
  if (integration.value == null) {
    return <span className='text-ThemeTextLight'>—</span>;
  }
  const display = formatDisplayValue(integration.value);
  const full = String(integration.value);
  if (display !== full) {
    return (
      <SimpleTooltip text={`${full}${integration.unit ? ` ${integration.unit}` : ''}`}>
        <span>{display}{renderValueUnit(integration.unit)}</span>
      </SimpleTooltip>
    );
  }
  return <>{integration.value}{renderValueUnit(integration.unit)}</>;
};

const renderSourceBadge = (source?: IntegrationValueSource, connectionInfo?: DataTransferConnectionInfo, detail?: string) => {
  const info = getSourceInfo(source, connectionInfo, detail);
  if (!info) {
    return null;
  }
  return (
    <SimpleTooltip text={info.tooltip}>
      <span className={`small ${info.color} ms-1`}>
        <i className={`fa ${info.icon} me-1`} />{info.label}
      </span>
    </SimpleTooltip>
  );
};

const renderToleranceIndicator = (toleranceResult?: ToleranceResult) => {
  if (!toleranceResult) {
    return null;
  }
  const { tolerance, actualDifference } = toleranceResult;
  const diffLabel = tolerance.type === 'percentage'
    ? `${actualDifference.toFixed(1)}%`
    : actualDifference.toFixed(2);
  const thresholdLabel = tolerance.type === 'percentage'
    ? `${tolerance.value}%`
    : String(tolerance.value);
  return (
    <SimpleTooltip text={`Differs by ${diffLabel} (within ${thresholdLabel} ${tolerance.type} tolerance)`}>
      <span className='small text-ThemeWarningMedium ms-1'>≈</span>
    </SimpleTooltip>
  );
};

const renderSurveyCell = (row: DataTransferRow, childInitiativeId: string) => {
  const surveyUrl = getSurveyUrl(row.survey, childInitiativeId);

  if (row.survey.status === SurveyDataStatus.Value) {
    const content = (
      <div>
        <div>{row.survey.value}{renderValueUnit(row.survey.unit)}{renderToleranceIndicator(row.toleranceResult)}</div>
        {row.survey.utrvStatus && <small className='text-ThemeTextLight'>{row.survey.utrvStatus}</small>}
      </div>
    );

    if (surveyUrl) {
      return (
        <a href={surveyUrl} target='_blank' rel='noopener noreferrer' className='text-decoration-none'>
          {content}
        </a>
      );
    }

    return content;
  }

  const label = getSurveyDataStatusLabel(row.survey.status);

  if (surveyUrl) {
    return (
      <a
        href={surveyUrl}
        target='_blank'
        rel='noopener noreferrer'
        className={getSurveyDataStatusClass(row.survey.status)}
      >
        {label} <i className='fa fa-external-link small' />
      </a>
    );
  }

  return (
    <span className={getSurveyDataStatusClass(row.survey.status)}>
      {label}
    </span>
  );
};

const renderNotices = (notices: ValidationNotice[], row?: Pick<DataTransferRow, 'internalNotes' | 'partnerNotes'>) => {
  const hasNotes = row?.internalNotes || row?.partnerNotes;
  if (notices.length === 0 && !hasNotes) {
    return <span className='text-ThemeSuccessMedium'><i className='fa fa-check' /></span>;
  }

  return (
    <div>
      {row?.internalNotes && (
        <div className='small text-ThemeWarningMedium' title={row.internalNotes}>
          <i className='fa fa-sticky-note me-1' />
          {row.internalNotes}
        </div>
      )}
      {row?.partnerNotes && (
        <div className='small text-ThemeIconSecondary' title={row.partnerNotes}>
          <i className='fa fa-envelope me-1' />
          {row.partnerNotes}
        </div>
      )}
      {notices.map((notice, i) => (
        <div key={i} className='small' title={notice.message}>
          <i className={`fa ${getNoticeIcon(notice.type)} me-1`} />
          {notice.message}
        </div>
      ))}
    </div>
  );
};

const renderGroupSurveySummary = (rows: DataTransferRow[]) => {
  const withValue = rows.filter((r) => r.survey.status === SurveyDataStatus.Value).length;
  const total = rows.length;

  if (withValue === 0) {
    const status = rows[0].survey.status;
    return (
      <span className={getSurveyDataStatusClass(status)}>
        {getSurveyDataStatusLabel(status)}
      </span>
    );
  }

  return (
    <span className={withValue === total ? 'text-ThemeSuccessMedium' : 'text-ThemeWarningMedium'}>
      {withValue}/{total} values
    </span>
  );
};

const renderGroupStatusSummary = (rows: DataTransferRow[]) => {
  const statusCounts = new Map<DataTransferMappingStatus, number>();
  for (const row of rows) {
    statusCounts.set(row.status, (statusCounts.get(row.status) ?? 0) + 1);
  }
  const verifiedCount = rows.filter((r) => r.verifiedAt).length;

  return (
    <div className='d-flex gap-1 flex-wrap align-items-center'>
      {Array.from(statusCounts.entries()).map(([status, count]) => (
        <span key={status} className={`badge ${getStatusBadgeClass(status)}`}>{count} {getStatusLabel(status)}</span>
      ))}
      {verifiedCount > 0 && (
        <span className='text-ThemeSuccessMedium small'>
          <i className='fa fa-check-circle' /> {verifiedCount} verified
        </span>
      )}
    </div>
  );
};

const renderGroupValidationSummary = (rows: DataTransferRow[]) => {
  const errorCount = rows.filter((r) => r.notices.some((n) => n.type === ValidationNoticeType.Error)).length;
  const warningCount = rows.filter((r) => r.notices.some((n) => n.type === ValidationNoticeType.Warning)).length;

  if (errorCount === 0 && warningCount === 0) {
    return <span className='text-ThemeSuccessMedium'><i className='fa fa-check' /></span>;
  }

  return (
    <div className='small'>
      {errorCount > 0 && (
        <div><i className='fa fa-times-circle text-ThemeDangerMedium me-1' />{errorCount} error{errorCount > 1 ? 's' : ''}</div>
      )}
      {warningCount > 0 && (
        <div><i className='fa fa-exclamation-triangle text-ThemeWarningMedium me-1' />{warningCount} warning{warningCount > 1 ? 's' : ''}</div>
      )}
    </div>
  );
};

const COLUMN_COUNT = 6;

const ExcludeToggle = ({ rowKey, isExcluded, onToggle }: { rowKey: string; isExcluded: boolean; onToggle: (key: string) => void }) => (
  <SimpleTooltip text={isExcluded ? 'Include in comparison' : 'Exclude from comparison'}>
    <i
      className={`fa ${isExcluded ? 'fa-eye-slash text-ThemeTextLight' : 'fa-eye text-ThemeIconSecondary'} cursor-pointer`}
      onClick={(e) => { e.stopPropagation(); onToggle(rowKey); }}
    />
  </SimpleTooltip>
);

const renderFlatRow = (row: DataTransferRow, childInitiativeId: string, excludedRows: Set<string>, onToggleExclude: (key: string) => void) => {
  const key = getRowKey(row);
  const isExcluded = excludedRows.has(key);
  return (
    <tr key={key} className={`${getRowClassName(row)} ${isExcluded ? 'opacity-50' : ''}`}>
      <td style={{ width: '30px' }}>
        <ExcludeToggle rowKey={key} isExcluded={isExcluded} onToggle={onToggleExclude} />
      </td>
      <td>
        <code className='small'>{row.utrCode}</code>
        <span className='small text-ThemeTextLight ms-2'>{row.valueType}</span>
        {renderUnitInfo(row.survey.unit, row.survey.numberScale)}
        <div>{row.utrLabel}</div>
        {row.optionCode && (
          <div className='small text-ThemeTextLight'>
            <i className='fa fa-columns me-1' />
            {row.optionLabel ?? row.optionCode}
          </div>
        )}
      </td>
      <td>{renderSurveyCell(row, childInitiativeId)}</td>
      <td>
        {renderIntegrationValue(row.integration)}
        {renderSourceBadge(row.integration.source, row.connectionInfo, row.integration.detail)}
        {renderToleranceIndicator(row.toleranceResult)}
      </td>
      <td>{renderStatusCell(row)}</td>
      <td>{renderNotices(row.notices, row)}</td>
    </tr>
  );
};

interface ExpandableGroupProps {
  group: UtrGroup;
  isExpanded: boolean;
  onToggle: () => void;
  childInitiativeId: string;
  excludedRows: Set<string>;
  onToggleExclude: (key: string) => void;
}

const ExpandableGroup = ({ group, isExpanded, onToggle, childInitiativeId, excludedRows, onToggleExclude }: ExpandableGroupProps) => (
  <Fragment>
    <tr className={`cursor-pointer ${getGroupClassName(group.rows)}`} onClick={onToggle}>
      <td className='text-center' style={{ width: '30px' }}>
        <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'} text-ThemeTextLight`} />
      </td>
      <td>
        <code className='small'>{group.utrCode}</code>
        <span className='small text-ThemeTextLight ms-2'>{group.valueType}</span>
        {renderUnitInfo(group.unit, group.numberScale)}
        <div className='fw-bold'>{group.utrLabel}</div>
        <span className='badge background-ThemeIconSecondary text-ThemeTextWhite'>{group.rows.length} options</span>
      </td>
      <td>{renderGroupSurveySummary(group.rows)}</td>
      <td>
        <span className='text-ThemeTextLight'>—</span>
      </td>
      <td>{renderGroupStatusSummary(group.rows)}</td>
      <td>{renderGroupValidationSummary(group.rows)}</td>
    </tr>
    {isExpanded && (
      <tr>
        <td colSpan={COLUMN_COUNT} className='p-0'>
          <table className='table table-sm mb-0 ms-4' style={{ width: 'calc(100% - 1.5rem)' }}>
            <thead>
              <tr className='small text-ThemeTextLight'>
                <th>Option</th>
                <th>Survey</th>
                <th>Integration</th>
                <th>Status</th>
                <th>Validation</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row) => {
                const key = getRowKey(row);
                const isExcluded = excludedRows.has(key);
                return (
                <tr key={key} className={`${getRowClassName(row)} ${isExcluded ? 'opacity-50' : ''}`}>
                  <td>
                    <ExcludeToggle rowKey={key} isExcluded={isExcluded} onToggle={onToggleExclude} />
                    <code className='small ms-2'>{row.optionCode}</code>
                    {row.optionLabel && <span className='ms-2 small'>{row.optionLabel}</span>}
                    {renderUnitInfo(row.survey.unit, row.survey.numberScale)}
                  </td>
                  <td>{renderSurveyCell(row, childInitiativeId)}</td>
                  <td>
                    {renderIntegrationValue(row.integration)}
                    {renderSourceBadge(row.integration.source, row.connectionInfo, row.integration.detail)}
                    {renderToleranceIndicator(row.toleranceResult)}
                  </td>
                  <td>{renderStatusCell(row)}</td>
                  <td>{renderNotices(row.notices, row)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </Fragment>
);

interface DataTransferTableProps {
  rows: DataTransferRow[];
  childInitiativeId: string;
  excludedRows: Set<string>;
  onToggleExclude: (key: string) => void;
}

export const DataTransferTable = ({ rows, childInitiativeId, excludedRows, onToggleExclude }: DataTransferTableProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const groups = useMemo(() => groupRowsByUtr(rows), [rows]);
  const expandableGroups = useMemo(() => groups.filter((g) => g.rows.length > 1), [groups]);
  const allExpanded = expandableGroups.length > 0 && expandableGroups.every((g) => expandedGroups.has(g.utrCode));

  const toggleGroup = (utrCode: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(utrCode)) {
        next.delete(utrCode);
      } else {
        next.add(utrCode);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(expandableGroups.map((g) => g.utrCode)));
    }
  };

  return (
    <div className='table-responsive'>
      <table className='table table-bordered table-sm align-middle'>
        <thead className='sticky-top z-auto background-ThemeBgDefault'>
          <tr>
            <th style={{ width: '30px' }}>
              {expandableGroups.length > 0 && (
                <i
                  className={`fa fa-chevron-${allExpanded ? 'down' : 'right'} text-ThemeTextLight cursor-pointer`}
                  onClick={toggleAll}
                />
              )}
            </th>
            <th>Metric</th>
            <th>Survey</th>
            <th>Integration</th>
            <th>Status</th>
            <th>Validation</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            if (group.rows.length === 1) {
              return renderFlatRow(group.rows[0], childInitiativeId, excludedRows, onToggleExclude);
            }

            return (
              <ExpandableGroup
                key={group.utrCode}
                group={group}
                isExpanded={expandedGroups.has(group.utrCode)}
                onToggle={() => toggleGroup(group.utrCode)}
                childInitiativeId={childInitiativeId}
                excludedRows={excludedRows}
                onToggleExclude={onToggleExclude}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
